import { z } from "zod";

import { ImpulseFormOptional, ImpulseFormShape, ImpulseFormSwitch, ImpulseFormUnit } from "~/tools/impulse-form";
import { isDeepEqual } from "~/tools/is-deep-equal";
import { isStringDirty } from "~/tools/is-string-dirty";
import { trim } from "~/tools/trim";

import {
	type DashboardListVariable,
	type DashboardListVariableSort,
	type DashboardListVariableValue,
	type DashboardVariableSource,
	dashboardListVariableSortSchema,
	narrowRawDashboardListVariable,
} from "../../../common/dashboard-spec";
import type { FormProcessor } from "../_form-processor";

import {
	MetricLabelValuesVariableFormProcessor,
	type MetricLabelValuesVariableFormState,
} from "./_metric-label-values-variable";
import { MetricPromQLVariableFormProcessor, type MetricPromQLVariableFormState } from "./_metric-promql-variable";
import { SeriesSelectorsFormProcessor, type SeriesSelectorsFormState } from "./_series-selectors";
import { StaticListVariableFormProcessor, type StaticListVariableFormState } from "./_static-list-variable";

export type FormState = ImpulseFormShape<{
	allowMultiple: ImpulseFormUnit<boolean>;
	defaultValue: ImpulseFormUnit<undefined | DashboardListVariableValue>;
	allValue: ImpulseFormOptional<ImpulseFormUnit<boolean>, ImpulseFormUnit<string>>;
	sort: ImpulseFormUnit<string, ReadonlyArray<string>, undefined | DashboardListVariableSort>;
	source: ImpulseFormSwitch<
		ImpulseFormUnit<string, ReadonlyArray<string>, DashboardVariableSource>,
		{
			MetricLabelNames: SeriesSelectorsFormState;
			MetricLabelValues: MetricLabelValuesVariableFormState;
			MetricPromQL: MetricPromQLVariableFormState;
			StaticList: StaticListVariableFormState;
		}
	>;
}>;

export const Sort = dashboardListVariableSortSchema.enum;

export const SORT_OPTIONS = [
	Sort.None,
	Sort.AlphabeticalAsc,
	Sort.AlphabeticalDesc,
	Sort.NumericalAsc,
	Sort.NumericalDesc,
	Sort.AlphabeticalCiAsc,
	Sort.AlphabeticalCiDesc,
] as const;

export const SOURCE_OPTIONS = [
	"MetricLabelNames",
	"MetricLabelValues",
	"MetricPromQL",
	"StaticList",
] as const satisfies ReadonlyArray<DashboardVariableSource>;

export const Processor: FormProcessor<FormState, undefined | DashboardListVariable> = {
	fromSpec: (spec) =>
		ImpulseFormShape({
			allowMultiple: ImpulseFormUnit(spec?.allowMultiple ?? false),
			defaultValue: ImpulseFormUnit(spec?.defaultValue ?? undefined, {
				isInputEqual: isDeepEqual,
			}),
			allValue: ImpulseFormOptional(
				ImpulseFormUnit(spec?.allowAllValue ?? false),
				ImpulseFormUnit(spec?.customAllValue ?? "", {
					isInputDirty: isStringDirty,
					transform: trim,
				}),
			),
			sort: ImpulseFormUnit(spec?.sort ?? SORT_OPTIONS[0], {
				schema: dashboardListVariableSortSchema,
			}),
			source: ImpulseFormSwitch(
				ImpulseFormUnit(spec?.plugin.kind ?? SOURCE_OPTIONS[0], {
					schema: z.enum(SOURCE_OPTIONS),
				}),
				{
					MetricLabelNames: SeriesSelectorsFormProcessor.fromSpec(
						spec?.plugin.kind === "MetricLabelNames" ? spec.plugin.spec.matchers : undefined,
					),

					MetricLabelValues: MetricLabelValuesVariableFormProcessor.fromSpec(
						spec?.plugin.kind === "MetricLabelValues" ? spec.plugin.spec : undefined,
					),

					MetricPromQL: MetricPromQLVariableFormProcessor.fromSpec(
						spec?.plugin.kind === "MetricPromQL" ? spec.plugin.spec : undefined,
					),

					StaticList: StaticListVariableFormProcessor.fromSpec(
						spec?.plugin.kind === "StaticList" ? spec.plugin.spec : undefined,
					),
				},
			),
		}),

	toSpec: ({ allowMultiple, allValue, sort, source, defaultValue }) => {
		const toPlugin = () => {
			if (source.kind === "MetricLabelNames") {
				return {
					kind: source.kind,
					spec: { matchers: SeriesSelectorsFormProcessor.toSpec(source.value) },
				};
			}

			if (source.kind === "MetricLabelValues") {
				return {
					kind: source.kind,
					spec: MetricLabelValuesVariableFormProcessor.toSpec(source.value),
				};
			}

			if (source.kind === "MetricPromQL") {
				return {
					kind: source.kind,
					spec: MetricPromQLVariableFormProcessor.toSpec(source.value),
				};
			}

			return {
				kind: source.kind,
				spec: StaticListVariableFormProcessor.toSpec(source.value),
			};
		};

		return narrowRawDashboardListVariable({
			allowMultiple,
			allowAllValue: allValue != null,
			customAllValue: (allValue ?? "") || undefined,
			defaultValue,
			sort,
			plugin: toPlugin(),
		});
	},
};
